// Pure calculation helpers for the S1000D Cost Estimator.
import { DM_TYPES, ILLUSTRATION_LEVELS, BR_CATEGORIES, SCHEDULE_PHASES } from "./s1000dData";

const LEVEL_KEYS = ["simple", "intermediate", "complex"];

export function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function computeDmHours(dmMatrix) {
  let dmHours = 0;
  const perTypeHours = {};
  const perLevelHours = { simple: 0, intermediate: 0, complex: 0 };

  for (const t of DM_TYPES) {
    const row = dmMatrix[t.code] || {};
    let typeHours = 0;
    for (const lvl of LEVEL_KEYS) {
      const cell = row[lvl] || { qty: 0, hours: 0 };
      const h = num(cell.qty) * num(cell.hours);
      typeHours += h;
      perLevelHours[lvl] += h;
    }
    perTypeHours[t.code] = typeHours;
    dmHours += typeHours;
  }
  return { dmHours, perTypeHours, perLevelHours };
}

export function computeIllustrationHours(illustrations) {
  let illusHours = 0;
  const perLevelHours = { simple: 0, intermediate: 0, complex: 0 };
  for (const lvl of ILLUSTRATION_LEVELS) {
    const cell = illustrations[lvl.key] || { qty: 0, hours: 0 };
    const h = num(cell.qty) * num(cell.hours);
    illusHours += h;
    perLevelHours[lvl.key] = h;
  }
  return { illusHours, perLevelHours };
}

export function computeRoleCosts(totalHours, roles) {
  const totalEffort = roles.reduce((s, r) => s + num(r.effortPercent), 0) || 1;
  let totalCost = 0;
  const breakdown = roles.map((r) => {
    const fraction = num(r.effortPercent) / 100;
    const roleHours = totalHours * fraction;
    const roleCost = roleHours * num(r.hourlyRate);
    totalCost += roleCost;
    return {
      name: r.name,
      hourlyRate: num(r.hourlyRate),
      effortPercent: num(r.effortPercent),
      roleHours,
      roleCost,
    };
  });
  // recompute percent share of cost
  const withPercent = breakdown.map((b) => ({
    ...b,
    costPercent: totalCost > 0 ? (b.roleCost / totalCost) * 100 : 0,
  }));
  return { totalCost, roleBreakdown: withPercent, totalEffortPercent: totalEffort };
}

export function computeTotals(estimate) {
  const dm = computeDmHours(estimate.dmMatrix);
  const il = computeIllustrationHours(estimate.illustrations);
  const totalHours = dm.dmHours + il.illusHours;
  const role = computeRoleCosts(totalHours, estimate.roles);
  const baseCost = role.totalCost;
  const overheadPct = num(estimate.overheadPercent);
  const contingencyPct = num(estimate.contingencyPercent);
  const overheadCost = baseCost * (overheadPct / 100);
  const contingencyCost = (baseCost + overheadCost) * (contingencyPct / 100);
  const grandTotal = baseCost + overheadCost + contingencyCost;
  const blendedRate = totalHours > 0 ? baseCost / totalHours : 0;

  // DM count
  let totalDms = 0;
  for (const t of DM_TYPES) {
    const row = estimate.dmMatrix[t.code] || {};
    for (const lvl of LEVEL_KEYS) totalDms += num(row[lvl]?.qty);
  }
  let totalIllus = 0;
  for (const lvl of ILLUSTRATION_LEVELS) {
    totalIllus += num(estimate.illustrations[lvl.key]?.qty);
  }

  return {
    dmHours: dm.dmHours,
    illusHours: il.illusHours,
    totalHours,
    baseCost,
    overheadCost,
    contingencyCost,
    grandTotal,
    totalCost: grandTotal, // backward-compat alias for header/footer
    blendedRate,
    totalDms,
    totalIllus,
    totalEffortPercent: role.totalEffortPercent,
    perTypeHours: dm.perTypeHours,
    perLevelHours: dm.perLevelHours,
    illusPerLevelHours: il.perLevelHours,
    roleBreakdown: role.roleBreakdown,
  };
}

// Active locale for the lower-level formatters. Updated by EstimateContext
// whenever the i18n language changes so any direct call to fmtHours / fmtPercent
// / fmtCurrency without an explicit locale arg still respects the current language.
let ACTIVE_LOCALE = "en-US";
export function setActiveLocale(code) { ACTIVE_LOCALE = code || "en-US"; }
export function getActiveLocale() { return ACTIVE_LOCALE; }

export const fmtCurrency = (v, symbol = "$", locale = ACTIVE_LOCALE) =>
  symbol +
  Number(v || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const fmtHours = (v, locale = ACTIVE_LOCALE) =>
  Number(v || 0).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export const fmtPercent = (v, locale = ACTIVE_LOCALE) =>
  Number(v || 0).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }) + "%";

// ---- Resource planning helpers ----
export function computeWorkingDays(startISO, endISO, weekendDays = [0, 6], publicHolidays = 0) {
  const empty = { calendarDays: 0, weekendsCount: 0, workingDays: 0, valid: false };
  if (!startISO || !endISO) return empty;
  const start = new Date(startISO + "T00:00:00");
  const end = new Date(endISO + "T00:00:00");
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return empty;
  const wkSet = new Set((weekendDays || []).map(Number));
  let calendarDays = 0;
  let weekendsCount = 0;
  // Cap iteration to a generous 20-year window to be safe
  const maxDays = 365 * 20;
  const cursor = new Date(start);
  while (cursor <= end && calendarDays < maxDays) {
    calendarDays++;
    if (wkSet.has(cursor.getDay())) weekendsCount++;
    cursor.setDate(cursor.getDate() + 1);
  }
  const workingDays = Math.max(0, calendarDays - weekendsCount - (Number(publicHolidays) || 0));
  return { calendarDays, weekendsCount, workingDays, valid: true };
}

export function computeResourcing(totals, plan) {
  const wd = computeWorkingDays(plan?.startDate, plan?.endDate, plan?.weekendDays, plan?.publicHolidays);
  const standardHrs = num(plan?.standardHoursPerDay) || 8;
  const effectiveHrs = num(plan?.effectiveHoursPerDay) || 6;
  const utilisation = standardHrs > 0 ? effectiveHrs / standardHrs : 0;
  const availableStandardHours = wd.workingDays * standardHrs;
  const availableEffectiveHours = wd.workingDays * effectiveHrs;
  const totalProjectHours = totals?.totalHours || 0;
  const fteEffective = availableEffectiveHours > 0 ? totalProjectHours / availableEffectiveHours : 0;
  const fteStandard = availableStandardHours > 0 ? totalProjectHours / availableStandardHours : 0;
  const roleFteBreakdown = (totals?.roleBreakdown || []).map((r) => ({
    name: r.name,
    roleHours: r.roleHours,
    effortPercent: r.effortPercent,
    fteEffective: availableEffectiveHours > 0 ? r.roleHours / availableEffectiveHours : 0,
    fteStandard: availableStandardHours > 0 ? r.roleHours / availableStandardHours : 0,
    headcount: availableEffectiveHours > 0 ? Math.ceil(r.roleHours / availableEffectiveHours) : 0,
  }));
  const totalHeadcount = roleFteBreakdown.reduce((s, r) => s + r.headcount, 0);
  return {
    ...wd,
    standardHoursPerDay: standardHrs,
    effectiveHoursPerDay: effectiveHrs,
    utilisation,
    availableStandardHours,
    availableEffectiveHours,
    totalProjectHours,
    fteEffective,
    fteStandard,
    totalHeadcount,
    roleFteBreakdown,
  };
}

export const fmtFte = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Helper that returns project + BREX totals optionally merged into a single grand total.
// When merge is on, OH/Cont % are re-applied to the combined base so they are not double-counted.
export function combineProjectAndBrex(totals, brex, overheadPercent = 0, contingencyPercent = 0, mergeBrex = false, dml = null, review = null) {
  let baseCost = num(totals.baseCost);
  let hours = num(totals.totalHours);

  const includeBrex = mergeBrex && brex;
  if (includeBrex) {
    baseCost += num(brex.baseCost);
    hours += num(brex.totalHours);
  }

  // Always include DML and Review costs when present
  if (dml) {
    baseCost += num(dml.baseCost);
    hours += num(dml.totalHours);
  }
  if (review) {
    baseCost += num(review.baseCost);
    hours += num(review.totalReviewHours);
  }

  const overheadCost = baseCost * (num(overheadPercent) / 100);
  const contingencyCost = (baseCost + overheadCost) * (num(contingencyPercent) / 100);
  const grandTotal = baseCost + overheadCost + contingencyCost;

  return {
    mergeBrex: !!includeBrex,
    combinedHours: hours,
    combinedBaseCost: baseCost,
    combinedOverheadCost: overheadCost,
    combinedContingencyCost: contingencyCost,
    combinedGrandTotal: grandTotal,
    brexShareHours: brex ? brex.totalHours : 0,
    brexShareGrandTotal: brex ? brex.grandTotal : 0,
    dmlShareHours: dml ? dml.totalHours : 0,
    dmlShareCost: dml ? dml.baseCost : 0,
    reviewShareHours: review ? review.totalReviewHours : 0,
    reviewShareCost: review ? review.baseCost : 0,
  };
}

// Lightweight per-issue compare for BREX. Returns delta rows by category and total.
export function brexIssueDelta(brexPlan, baseIssue, targetIssue) {
  const defineMin = num(brexPlan?.defineMinutesPerDP);
  const brexMin = num(brexPlan?.brexMinutesPerDP);
  const perDp = defineMin + brexMin;
  const rows = BR_CATEGORIES.map((cat) => {
    const baseDps = cat.dpCounts?.[baseIssue] ?? 0;
    const targetDps = cat.dpCounts?.[targetIssue] ?? 0;
    const delta = targetDps - baseDps;
    return {
      id: cat.id,
      name: cat.name,
      baseDps,
      targetDps,
      deltaDps: delta,
      deltaHours: (delta * perDp) / 60,
    };
  });
  const totalBaseDps = rows.reduce((s, r) => s + r.baseDps, 0);
  const totalTargetDps = rows.reduce((s, r) => s + r.targetDps, 0);
  const totalDeltaDps = totalTargetDps - totalBaseDps;
  return {
    baseIssue,
    targetIssue,
    rows,
    totalBaseDps,
    totalTargetDps,
    totalDeltaDps,
    totalDeltaHours: (totalDeltaDps * perDp) / 60,
  };
}

// ---- Data Management List (DML) ----
export function computeDml(dmlPlan, totals, blendedRate, overheadPercent = 0, contingencyPercent = 0) {
  if (!dmlPlan) return null;
  const dmcCount = num(dmlPlan.dmcCount) || num(totals?.totalDms);
  const icnCount = num(dmlPlan.icnCount) || num(totals?.totalIllus);
  const pmcCount = num(dmlPlan.pmcCount);
  const mDmc = num(dmlPlan.minutesPerDmc);
  const mIcn = num(dmlPlan.minutesPerIcn);
  const mPmc = num(dmlPlan.minutesPerPmc);
  const dmcMinutes = dmcCount * mDmc;
  const icnMinutes = icnCount * mIcn;
  const pmcMinutes = pmcCount * mPmc;
  const totalMinutes = dmcMinutes + icnMinutes + pmcMinutes;
  const totalHours = totalMinutes / 60;
  const rate = num(blendedRate);
  const baseCost = totalHours * rate;
  const overheadCost = baseCost * (num(overheadPercent) / 100);
  const contingencyCost = (baseCost + overheadCost) * (num(contingencyPercent) / 100);
  const grandTotal = baseCost + overheadCost + contingencyCost;
  return {
    dmcCount, icnCount, pmcCount,
    minutesPerDmc: mDmc, minutesPerIcn: mIcn, minutesPerPmc: mPmc,
    dmcMinutes, icnMinutes, pmcMinutes,
    totalMinutes, totalHours,
    blendedRate: rate, baseCost, overheadCost, contingencyCost, grandTotal,
  };
}

export function computeBrex(brexPlan, overheadPercent = 0, contingencyPercent = 0) {
  if (!brexPlan) return null;
  const issue = brexPlan.s1000dIssue || "6.0";
  const defineMin = num(brexPlan.defineMinutesPerDP);
  const brexMin = num(brexPlan.brexMinutesPerDP);

  let totalDps = 0;
  let includedCategoryCount = 0;
  const categoryRows = BR_CATEGORIES.map((cat) => {
    const baseDps = cat.dpCounts?.[issue] ?? 0;
    const overrideRaw = brexPlan.categoryDpOverrides?.[cat.id];
    const dps = overrideRaw === undefined || overrideRaw === "" ? baseDps : num(overrideRaw);
    const included = brexPlan.categoryInclusions?.[cat.id] !== false;
    if (included) {
      totalDps += dps;
      includedCategoryCount += 1;
    }
    const defineMinutes = included ? dps * defineMin : 0;
    const brexMinutes = included ? dps * brexMin : 0;
    return {
      id: cat.id,
      name: cat.name,
      description: cat.description,
      dps,
      defaultDps: baseDps,
      included,
      defineMinutes,
      brexMinutes,
      totalMinutes: defineMinutes + brexMinutes,
    };
  });

  const defineMinutesTotal = totalDps * defineMin;
  const brexMinutesTotal = totalDps * brexMin;
  const totalMinutes = defineMinutesTotal + brexMinutesTotal;
  const defineHours = defineMinutesTotal / 60;
  const brexHours = brexMinutesTotal / 60;
  const totalHours = totalMinutes / 60;

  const roles = Array.isArray(brexPlan.roles) ? brexPlan.roles : [];
  const totalEffort = roles.reduce((s, r) => s + num(r.effortPercent), 0) || 0;
  let baseCost = 0;
  const roleBreakdown = roles.map((r) => {
    const fraction = num(r.effortPercent) / 100;
    const roleHours = totalHours * fraction;
    const roleCost = roleHours * num(r.hourlyRate);
    baseCost += roleCost;
    return {
      name: r.name,
      hourlyRate: num(r.hourlyRate),
      effortPercent: num(r.effortPercent),
      roleHours,
      roleCost,
    };
  });
  const overheadCost = baseCost * (num(overheadPercent) / 100);
  const contingencyCost = (baseCost + overheadCost) * (num(contingencyPercent) / 100);
  const grandTotal = baseCost + overheadCost + contingencyCost;
  const blendedRate = totalHours > 0 ? baseCost / totalHours : 0;

  return {
    issue,
    totalDps,
    includedCategoryCount,
    defineMinutesTotal,
    brexMinutesTotal,
    totalMinutes,
    defineHours,
    brexHours,
    totalHours,
    baseCost,
    overheadCost,
    contingencyCost,
    grandTotal,
    blendedRate,
    totalEffortPercent: totalEffort,
    categoryRows,
    roleBreakdown: roleBreakdown.map((b) => ({
      ...b,
      costPercent: baseCost > 0 ? (b.roleCost / baseCost) * 100 : 0,
    })),
  };
}


// ---- Risk & Assumptions ----
export function computeRisk(riskPlan) {
  if (!riskPlan) {
    return {
      risks: [], assumptionsCount: 0, dependenciesCount: 0,
      count: 0, totalScore: 0, avgScore: 0, maxScore: 0,
      exposure: 0, suggestedContingencyBump: 0,
      breakdown: { low: 0, medium: 0, high: 0, critical: 0 },
      autoContingency: false,
    };
  }
  const risks = (riskPlan.risks || []).map((r) => {
    const lk = Math.max(0, Math.min(5, num(r.likelihood)));
    const im = Math.max(0, Math.min(5, num(r.impact)));
    const score = lk * im;
    let band = "low";
    if (score >= 16) band = "critical";
    else if (score >= 10) band = "high";
    else if (score >= 5) band = "medium";
    return { ...r, likelihood: lk, impact: im, score, band };
  });
  const count = risks.length;
  const totalScore = risks.reduce((s, r) => s + r.score, 0);
  const maxScore = risks.reduce((m, r) => Math.max(m, r.score), 0);
  const avgScore = count > 0 ? totalScore / count : 0;
  // Exposure normalised 0..1: weight critical heavily
  const exposure = count > 0 ? Math.min(1, totalScore / (25 * count)) : 0;
  const cap = num(riskPlan.maxAutoBumpPercent) || 20;
  // Bump curve: exposure^0.7 * cap so even moderate risk produces some bump
  const suggestedContingencyBump = riskPlan.autoContingency
    ? Math.round(Math.pow(exposure, 0.7) * cap * 10) / 10
    : 0;
  const breakdown = {
    low: risks.filter((r) => r.band === "low").length,
    medium: risks.filter((r) => r.band === "medium").length,
    high: risks.filter((r) => r.band === "high").length,
    critical: risks.filter((r) => r.band === "critical").length,
  };
  return {
    risks,
    assumptionsCount: (riskPlan.assumptions || []).length,
    dependenciesCount: (riskPlan.dependencies || []).length,
    count, totalScore, avgScore, maxScore,
    exposure, suggestedContingencyBump,
    breakdown, autoContingency: !!riskPlan.autoContingency,
  };
}

// ---- Effort-by-Phase / Schedule ----

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(key) {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}
function addDays(d, n) {
  const c = new Date(d); c.setDate(c.getDate() + n); return c;
}
function daysBetween(a, b) {
  return Math.max(0, Math.round((b - a) / 86400000));
}

export function computeSchedule(schedulePlan, totalHours, projectStartISO, projectEndISO) {
  if (!schedulePlan) return { phases: [], months: [], totalAllocated: 0, totalPercent: 0, hasDates: false };

  const phases = SCHEDULE_PHASES.map((p) => {
    const cfg = schedulePlan.phases?.[p.key] || { percent: 0, startOffsetWeeks: 0, durationWeeks: 0 };
    const percent = num(cfg.percent);
    const startOffsetWeeks = num(cfg.startOffsetWeeks);
    const durationWeeks = num(cfg.durationWeeks);
    const hours = (num(totalHours) * percent) / 100;
    return { ...p, percent, startOffsetWeeks, durationWeeks, hours };
  });
  const totalPercent = phases.reduce((s, p) => s + p.percent, 0);
  const totalAllocated = phases.reduce((s, p) => s + p.hours, 0);

  const hasDates = !!projectStartISO;
  const months = [];
  const phaseTimeline = [];

  if (hasDates) {
    const start = new Date(projectStartISO + "T00:00:00");
    // Compute per-phase calendar window
    const phaseWindows = phases.map((p) => {
      const ps = addDays(start, p.startOffsetWeeks * 7);
      const pe = addDays(ps, p.durationWeeks * 7);
      return { ...p, startDate: ps, endDate: pe };
    });
    phaseTimeline.push(...phaseWindows);

    // Build month grid covering all phase windows (and within project window if end set)
    const minDate = phaseWindows.reduce((m, p) => (p.startDate < m ? p.startDate : m), phaseWindows[0]?.startDate || start);
    let maxDate = phaseWindows.reduce((m, p) => (p.endDate > m ? p.endDate : m), phaseWindows[0]?.endDate || start);
    if (projectEndISO) {
      const pe = new Date(projectEndISO + "T00:00:00");
      if (pe > maxDate) maxDate = pe;
    }
    // Walk month-by-month from minDate to maxDate (inclusive)
    const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const last = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    const monthKeys = [];
    while (cursor <= last) {
      monthKeys.push(monthKey(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }

    for (const mk of monthKeys) {
      const [yy, mm] = mk.split("-").map(Number);
      const monthStart = new Date(yy, mm - 1, 1);
      const monthEnd = new Date(yy, mm, 0); // last day of month
      const entry = { key: mk, label: monthLabel(mk), totalHours: 0, byPhase: {} };
      for (const pw of phaseWindows) {
        const ovStart = pw.startDate > monthStart ? pw.startDate : monthStart;
        const ovEnd = pw.endDate < monthEnd ? pw.endDate : monthEnd;
        const overlapDays = daysBetween(ovStart, addDays(ovEnd, 1)); // inclusive
        const phaseDays = daysBetween(pw.startDate, pw.endDate);
        const fraction = phaseDays > 0 ? overlapDays / phaseDays : 0;
        const hrs = pw.hours * fraction;
        entry.byPhase[pw.key] = hrs;
        entry.totalHours += hrs;
      }
      months.push(entry);
    }
  }

  return {
    phases,
    phaseTimeline,
    months,
    totalAllocated,
    totalPercent,
    hasDates,
  };
}

// ---- Review & Update costing ----
export function computeReview(totals, reviewPlan) {
  if (!reviewPlan) return null;
  const pcts = Array.isArray(reviewPlan.percentages) ? reviewPlan.percentages : [25, 50, 75];
  const blendedRate = num(totals?.blendedRate);

  let totalReviewHours = 0;
  // DM types
  for (const t of DM_TYPES) {
    const hrs = num(totals?.perTypeHours?.[t.code]);
    if (hrs > 0) {
      for (const p of pcts) totalReviewHours += hrs * (num(p) / 100);
    }
  }
  // Illustrations
  const illusHrs = num(totals?.illusHours);
  if (illusHrs > 0) {
    for (const p of pcts) totalReviewHours += illusHrs * (num(p) / 100);
  }

  const baseCost = totalReviewHours * blendedRate;
  return { totalReviewHours, baseCost, blendedRate, pcts };
}

// ---- Bid Price / Win Probability ----
export function computeBidPrice(cost, bidPlan) {
  const c = num(cost);
  const margin = num(bidPlan?.targetMarginPercent);
  const winProb = num(bidPlan?.winProbabilityPercent);
  const competitor = num(bidPlan?.competitorPrice);
  const strategicAdj = num(bidPlan?.strategicAdjustmentPercent);

  const targetPrice = margin >= 100 ? c : c / (1 - margin / 100);
  const marginAmount = targetPrice - c;
  const adjustedPrice = targetPrice * (1 + strategicAdj / 100);
  const adjustedMargin = adjustedPrice > 0 ? ((adjustedPrice - c) / adjustedPrice) * 100 : 0;
  const expectedValue = adjustedPrice * (winProb / 100);
  const expectedMargin = expectedValue - c * (winProb / 100);
  const competitorDelta = competitor > 0 ? ((adjustedPrice - competitor) / competitor) * 100 : null;

  // Recommendation string
  let recommendation = "neutral";
  let recommendationLabel = "Market parity";
  if (competitor > 0) {
    if (adjustedPrice < competitor * 0.95) {
      recommendation = "aggressive";
      recommendationLabel = "Aggressive bid — likely below market";
    } else if (adjustedPrice > competitor * 1.05) {
      recommendation = "premium";
      recommendationLabel = "Premium positioning — justify with value";
    } else {
      recommendation = "neutral";
      recommendationLabel = "At market — typical win rates apply";
    }
  } else if (adjustedMargin < 10) {
    recommendation = "low-margin";
    recommendationLabel = "Margin under 10% — consider cost reduction";
  } else if (adjustedMargin > 35) {
    recommendation = "high-margin";
    recommendationLabel = "Margin over 35% — confirm market will accept";
  }

  return {
    cost: c,
    margin,
    winProb,
    competitor,
    strategicAdj,
    targetPrice,
    marginAmount,
    adjustedPrice,
    adjustedMargin,
    expectedValue,
    expectedMargin,
    competitorDelta,
    recommendation,
    recommendationLabel,
  };
}
